// DeployShortcode.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { dbModernCasinos, dbHrefsets } from '../../App';
import { useLoader } from '../../universal/context/LoaderContext';
import { useNotification } from '../../universal/context/NotificationContext';

interface ISite {
  siteId: string;
  siteName: string;
  siteURL: string;
  category: string;
  image?: string;
  imageName?: string;
  bonus?: string;
  termsPlain?: string;
  termsLinks?: string[];
  term1?: string;
  term2?: string;
  term3?: string;
  gotoLink?: string;
  trackingLink?: string;
  imageAlt?: string;
  firebaseImageUrl?: string;
  mainTermsText?: string;
}

interface IDeployedSite extends ISite {
  deploymentId: string;
  saveId?: string;
  style?: string;
  reviewLinks?: boolean;
}

const DeployShortcode: React.FC = () => {
  const { id: shortcode } = useParams<{ id: string }>();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();

  // Existing state
  const [deployedSites, setDeployedSites] = useState<IDeployedSite[]>([]);
  const [fullSites, setFullSites] = useState<ISite[]>([]);
  const [filteredSites, setFilteredSites] = useState<ISite[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [currentSaveId, setCurrentSaveId] = useState<string | null>(null);

  // Modal states
  const [showStyleModal, setShowStyleModal] = useState<boolean>(false);
  const [showReviewLinksModal, setShowReviewLinksModal] = useState<boolean>(false);

  // Deployment buffers
  const [casinosFromCodebaseF, setCasinosFromCodebaseF] = useState<ISite[]>([]);
  const [selectedSite, setSelectedSite] = useState<ISite | null>(null);

  // Global defaults from parent doc
  const [headerTexts, setHeaderTexts] = useState({
    posText: '',
    casinoText: '',
    casinoBonusText: '',
    moreInfoText: '',
    moveOnText: ''
  });
  const [ctaButtonText, setCtaButtonText] = useState('');
  const [loadMoreButtonText, setLoadMoreButtonText] = useState('');
  const [savedStyle, setSavedStyle] = useState<string>(''); 
  const [savedReviewLinks, setSavedReviewLinks] = useState<boolean | null>(null);

  // NEW: Global GEO and Page Slug from parent document
  const [geoSlug, setGeoSlug] = useState('');
  const [pageSlug, setPageSlug] = useState('');

  // Per-deployment settings
  const [deploymentStyle, setDeploymentStyle] = useState<string>('');
  const [deploymentReviewLinks, setDeploymentReviewLinks] = useState<boolean | null>(null);

  useEffect(() => {
    if (shortcode) {
      loadParentData();
      fetchCurrentSaveId();
      fetchDeployments();
      fetchFullSites();
      fetchCategories();
    }
  }, [shortcode]);

  async function fetchCurrentSaveId() {
    try {
      const snap = await getDoc(doc(dbModernCasinos, 'casinosets', shortcode!));
      if (snap.exists()) setCurrentSaveId((snap.data() as any).saveId || null);
    } catch (err: any) {
      console.error(err);
      showNotification(`Error fetching current saveId: ${err.message}`, 'error');
    }
  }

  async function loadParentData() {
    try {
      const snap = await getDoc(doc(dbModernCasinos, 'casinosets', shortcode!));
      if (!snap.exists()) return;
      const data: any = snap.data();
      setHeaderTexts(data.headerTexts || {});
      setCtaButtonText(data.ctaButtonText || '');
      setLoadMoreButtonText(data.loadMoreButtonText || '');
      setSavedStyle(data.style || '');
      setSavedReviewLinks(data.reviewLinks ?? null);
      setGeoSlug(data.geoSlug || '');
      setPageSlug(data.pageSlug || '');
    } catch (err: any) {
      console.error(err);
      showNotification(`Error loading parent doc: ${err.message}`, 'error');
    }
  }

  async function fetchDeployments() {
    showLoader("Fetching deployments...");
    try {
      const qs = await getDocs(collection(dbModernCasinos, 'casinosets', shortcode!, 'deployments'));
      const arr: IDeployedSite[] = [];
      for (const d of qs.docs) {
        const dd: any = d.data();
        const snap = await getDoc(doc(dbHrefsets, 'sites', dd.siteId));
        if (!snap.exists()) continue;
        const s = snap.data() as ISite;
        arr.push({
          ...s,
          deploymentId: d.id,
          saveId: dd.saveId,
          style: dd.style,
          reviewLinks: dd.reviewLinks
        });
      }
      arr.sort((a,b)=>a.siteName.localeCompare(b.siteName));
      setDeployedSites(arr);
    } catch (err) {
      console.error(err);
      showNotification(`Error fetching deployments: ${err}`, 'error');
    } finally {
      hideLoader();
    }
  }

  async function fetchFullSites() {
    try {
      const qs = await getDocs(collection(dbHrefsets, 'sites'));
      const sites = qs.docs.map(d => d.data() as ISite);
      setFullSites(sites);
      setFilteredSites(sites);
    } catch (err) {
      console.error(err);
      showNotification(`Error fetching full sites: ${err}`, 'error');
    }
  }

  async function fetchCategories() {
    try {
      const qs = await getDocs(collection(dbHrefsets, 'categories'));
      const cats = qs.docs.map(d => ({ id: d.id, ...d.data() } as any));
      setCategories(cats);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    let f = fullSites;
    if (searchQuery) {
      f = f.filter(s=>
        s.siteName.toLowerCase().includes(searchQuery.toLowerCase())||
        s.siteURL.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (categoryFilter) {
      f = f.filter(s=>s.category===categoryFilter);
    }
    const used = deployedSites.map(s=>s.siteId);
    f = f.filter(s=>!used.includes(s.siteId));
    setFilteredSites(f);
  }, [fullSites, searchQuery, categoryFilter, deployedSites]);

  // ─── Deploy flows ────────────────────────────────────────────────────────────

  /** First‐time deploy */
  async function handleFirstTimeDeploy(site: ISite) {
    showLoader("Fetching all casinos for deployment...");
    const qs = await getDocs(collection(dbModernCasinos,'casinosets',shortcode!,'casinos'));
    const all = qs.docs.map(d=>d.data() as ISite);
    hideLoader();

    setCasinosFromCodebaseF(all);
    setSelectedSite(site);
    setDeploymentStyle('');
    setDeploymentReviewLinks(null);
    setShowStyleModal(true);
  }

  /** Redeploy */
  async function handleRedeploy(site: IDeployedSite) {
    showLoader("Fetching all casinos for redeployment...");
    const qs = await getDocs(collection(dbModernCasinos,'casinosets',shortcode!,'casinos'));
    const all = qs.docs.map(d=>d.data() as ISite);
    hideLoader();

    setDeploymentStyle(site.style || '');
    setDeploymentReviewLinks(site.reviewLinks ?? false);
    await redeployShortcode(site, all);
  }

  // ─── Modal callbacks ────────────────────────────────────────────────────────

  function handleStyleSelection(style: string) {
    setShowStyleModal(false);
    setDeploymentStyle(style);
    setShowReviewLinksModal(true);
  }

  async function handleReviewLinksSelection(option: boolean) {
    setShowReviewLinksModal(false);
    setDeploymentReviewLinks(option);
    if (selectedSite) {
      await deployShortcode(option, selectedSite);
    }
  }

  // ─── Actual POSTs ───────────────────────────────────────────────────────────

  async function deployShortcode(reviewLinksOverride: boolean, site: ISite) {
    showLoader("Deploying shortcode...");
    try {
      const payload = {
        title: shortcode,
        style: deploymentStyle,
        reviewLinks: reviewLinksOverride,
        casinos: casinosFromCodebaseF,
        headerTexts, ctaButtonText, loadMoreButtonText,
        geoSlug, pageSlug
      };
      console.log("deployShortcode => payload:",payload);

      const res = await fetch(
        `${site.siteURL.replace(/\/+$/,'')}/wp-json/myplugin/v1/save-shortcode`,
        {
          method:'POST',
          headers:{
            'Content-Type':'application/json',
            'Authorization':'Bearer cf8b06e9-c323-4693-b1ca-e1fcdaf60625'
          },
          body: JSON.stringify(payload)
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.message||'Error deploying shortcode');

      showNotification(json.message,'success');
      const newSaveId = json.saveId||currentSaveId;
      await setDoc(
        doc(collection(dbModernCasinos,'casinosets',shortcode!,'deployments')),
        {
          siteId: site.siteId,
          saveId: newSaveId,
          style: deploymentStyle,
          reviewLinks: reviewLinksOverride
        },
        { merge:true }
      );
      if (newSaveId && newSaveId!==currentSaveId) {
        await updateDoc(doc(dbModernCasinos,'casinosets',shortcode!),{
          saveId:newSaveId,
          headerTexts,
          ctaButtonText,
          loadMoreButtonText,
          geoSlug,
          pageSlug
        });
        setCurrentSaveId(newSaveId);
      }
      fetchDeployments();
    } catch(err:any) {
      console.error(err);
      showNotification(`Error: ${err.message}`,'error');
    } finally {
      hideLoader();
      setCasinosFromCodebaseF([]);
      setSelectedSite(null);
      setDeploymentStyle('');
      setDeploymentReviewLinks(null);
    }
  }

  async function redeployShortcode(deployment: IDeployedSite, casinos: ISite[]) {
    showLoader("Redeploying shortcode...");
    try {
      const payload = {
        shortcode,
        style: deployment.style,
        reviewLinks: deployment.reviewLinks,
        casinos,
        headerTexts, ctaButtonText, loadMoreButtonText,
        geoSlug, pageSlug
      };
      console.log("redeployShortcode => payload:",payload);

      const res = await fetch(
        `${deployment.siteURL.replace(/\/+$/,'')}/wp-json/myplugin/v1/redeploy-shortcode`,
        {
          method:'POST',
          headers:{
            'Content-Type':'application/json',
            'Authorization':'Bearer cf8b06e9-c323-4693-b1ca-e1fcdaf60625'
          },
          body: JSON.stringify(payload)
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.message||'Error redeploying shortcode');

      showNotification(json.message,'success');
      const newSaveId = json.saveId||currentSaveId;
      await setDoc(
        doc(dbModernCasinos,'casinosets',shortcode!,'deployments',deployment.deploymentId),
        {
          saveId:newSaveId,
          style:deployment.style,
          reviewLinks:deployment.reviewLinks
        },
        { merge:true }
      );
      if (newSaveId && newSaveId!==currentSaveId) {
        await updateDoc(doc(dbModernCasinos,'casinosets',shortcode!),{
          saveId:newSaveId,
          headerTexts,
          ctaButtonText,
          loadMoreButtonText,
          geoSlug,
          pageSlug
        });
        setCurrentSaveId(newSaveId);
      }
      fetchDeployments();
    } catch(err:any) {
      console.error(err);
      showNotification(`Error: ${err.message}`,'error');
    } finally {
      hideLoader();
      setCasinosFromCodebaseF([]);
      setSelectedSite(null);
      setDeploymentStyle('');
      setDeploymentReviewLinks(null);
    }
  }

  // ─── Purge ─────────────────────────────────────────────────────────────────

  async function purgeDeployment() {
    if (!selectedSite) {
      showNotification('Missing site info for deletion.','error');
      return;
    }
    showLoader("Deleting shortcode on WP...");
    try {
      const res = await fetch(
        `${selectedSite.siteURL.replace(/\/+$/,'')}/wp-json/myplugin/v1/delete-shortcode`,
        {
          method:'POST',
          headers:{
            'Content-Type':'application/json',
            'Authorization':'Bearer cf8b06e9-c323-4693-b1ca-e1fcdaf60625'
          },
          body: JSON.stringify({ shortcode })
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.message||'Error deleting shortcode');
      showNotification(json.message,'success');
      // delete from Firestore
      const docs = await getDocs(collection(dbModernCasinos,'casinosets',shortcode!,'deployments'));
      await Promise.all(docs.docs.map(d=> deleteDoc(d.ref)));
      fetchDeployments();
    } catch(err:any) {
      console.error(err);
      showNotification(`Error: ${err.message}`,'error');
    } finally {
      hideLoader();
      setSelectedSite(null);
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="dashpanel">
      <div className="main">
        <div className="head">
          <h1>Deploy {shortcode} Shortcode</h1>
          <div style={{ marginBottom:'1rem',marginTop:'1rem' }}>
            <Link
              to="/console/shortcodes"
              style={{
                backgroundColor:'#007BFF',
                color:'#fff',
                fontWeight:'bold',
                display:'flex',
                width:'fit-content',
                padding:'10px 15px',
                borderRadius:'5px',
                textDecoration:'none'
              }}
            >
              Back to Shortcodes
            </Link>
          </div>

          {/* Deployment List */}
          <h2>Deployment List</h2>
          {deployedSites.length===0 ? (
            <p>No deployments found for this shortcode.</p>
          ) : (
            <div className="bubble-container" style={{ marginBottom:'2rem' }}>
              {deployedSites.map(site=>(
                <div key={site.deploymentId} className="bubble hrefsets">
                  <h2 style={{ fontSize:'12px' }}>{site.siteName}</h2>
                  <h3 style={{ fontSize:'10px' }}>({site.siteId})</h3>
                  <h3>{site.siteURL}</h3>
                  <p><strong>Category:</strong> {site.category}</p>
                  <p><strong>Style:</strong> {site.style||'Not set'}</p>
                  <p>
                    <strong>Review Links:</strong>{' '}
                    {typeof site.reviewLinks==='boolean'
                      ? site.reviewLinks?'Shown':'Hidden'
                      : 'Not set'}
                  </p>
                  <div className="buttonsetcontainer">
                    {site.saveId!==currentSaveId && (
                      <button
                        className="sitemanagerbutton redeploy"
                        style={{ marginLeft:0 }}
                        onClick={()=>handleRedeploy(site)}
                      >
                        Redeploy
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Full Site List */}
          <h2>Full Site List</h2>
          <div
            className="add-client-form edit-hreflang"
            style={{ width:350,padding:5,display:'flex',gap:'10px' }}
          >
            <input
              type="text"
              placeholder="Search Sites"
              value={searchQuery}
              onChange={e=>setSearchQuery(e.target.value)}
              style={{ marginBottom:0 }}
            />
            <select
              value={categoryFilter}
              onChange={e=>setCategoryFilter(e.target.value)}
              style={{ marginBottom:0,marginLeft:5 }}
            >
              <option value="">All Categories</option>
              {categories.map(cat=>(
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          {filteredSites.length===0 ? (
            <p>No sites found.</p>
          ) : (
            <div className="bubble-container">
              {filteredSites.map(site=>(
                <div key={site.siteId} className="bubble hrefsets">
                  <h2 style={{ fontSize:'12px' }}>{site.siteName}</h2>
                  <h3 style={{ fontSize:'10px' }}>({site.siteId})</h3>
                  <h3>{site.siteURL}</h3>
                  <p><strong>Category:</strong> {site.category}</p>
                  <div className="buttonsetcontainer">
                    <button
                      className="sitemanagerbutton selectedhref"
                      onClick={()=>handleFirstTimeDeploy(site)}
                    >
                      Deploy Entire Shortcode
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal for selecting table style */}
      {showStyleModal && (
        <div
          className="modal-overlay"
          style={{
            position:'fixed',
            top:0,left:0,right:0,bottom:0,
            backgroundColor:'rgba(0,0,0,0.5)',
            display:'flex',
            alignItems:'center',
            justifyContent:'center'
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor:'#fff',
              padding:'20px',
              borderRadius:'8px',
              textAlign:'center'
            }}
          >
            <h2>Select Table Style</h2>
            <div style={{ margin:'20px 0' }}>
              <button
                onClick={()=>handleStyleSelection('Zamsino Style')}
                style={{ marginRight:'10px',padding:'10px 15px' }}
              >
                Zamsino Style
              </button>
              <button
                onClick={()=>handleStyleSelection('Slot Style')}
                style={{ padding:'10px 15px' }}
              >
                Slot Style
              </button>
            </div>
            <button
              onClick={()=>{
                setShowStyleModal(false);
                setShowReviewLinksModal(false);
                setCasinosFromCodebaseF([]);
                setSelectedSite(null);
                setDeploymentStyle('');
                setDeploymentReviewLinks(null);
              }}
              style={{
                padding:'8px 12px',
                backgroundColor:'#ccc',
                border:'none',
                borderRadius:'4px',
                cursor:'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Modal for selecting review links option */}
      {showReviewLinksModal && (
        <div
          className="modal-overlay"
          style={{
            position:'fixed',
            top:0,left:0,right:0,bottom:0,
            backgroundColor:'rgba(0,0,0,0.5)',
            display:'flex',
            alignItems:'center',
            justifyContent:'center'
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor:'#fff',
              padding:'20px',
              borderRadius:'8px',
              textAlign:'center'
            }}
          >
            <h2>Review Links Option</h2>
            <p>Do you want the review links to be shown?</p>
            <div style={{ margin:'20px 0' }}>
              <button
                onClick={()=>handleReviewLinksSelection(true)}
                style={{ marginRight:'10px',padding:'10px 15px' }}
              >
                Show Review Links
              </button>
              <button
                onClick={()=>handleReviewLinksSelection(false)}
                style={{ padding:'10px 15px' }}
              >
                Hide Review Links
              </button>
            </div>
            <button
              onClick={()=>setShowReviewLinksModal(false)}
              style={{
                padding:'8px 12px',
                backgroundColor:'#ccc',
                border:'none',
                borderRadius:'4px',
                cursor:'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeployShortcode;
